import zipcodes from 'zipcodes'
import { ENROLLMENT_COMMON_MESSAGES } from '../../../constants/enrollmentContent'
import type { AddressFields } from '../types'

export const formatPhoneNumber = (value: string) => {
  const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
  if (!digitsOnly) return ''
  if (digitsOnly.length > 6) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`
  }
  if (digitsOnly.length > 3) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`
  }
  return `(${digitsOnly}`
}

export const hasValidPhoneNumber = (value: string) => value.replace(/\D/g, '').length === 10

export const normalizeEmail = (value?: string | null) => value?.trim().toLowerCase() ?? ''

export const formatBirthDate = (value: string) => {
  if (!value) return ''
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return value

  const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dateMatch) {
    const [, year, month, day] = dateMatch
    return `${month}/${day}/${year}`
  }

  return value
}

export const hasFormattedAddressDetails = (
  addressLine1: string,
  city: string,
  state: string,
  zipCode: string
) => {
  const normalizedAddress = addressLine1.toLowerCase()
  const normalizedCity = city.trim().toLowerCase()
  const normalizedState = state.trim().toLowerCase()
  const normalizedZip = zipCode.trim().toLowerCase()

  if (!normalizedAddress) return false

  return (
    normalizedAddress.includes(',') &&
    ((normalizedCity && normalizedAddress.includes(normalizedCity)) ||
      (normalizedState && normalizedAddress.includes(normalizedState)) ||
      (normalizedZip && normalizedAddress.includes(normalizedZip)) ||
      normalizedAddress.includes(' usa'))
  )
}

const parseBirthDate = (value: string) => {
  if (!value) return null

  const usFormat = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (usFormat) {
    const [, month, day, year] = usFormat
    const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))
    if (
      Number.isNaN(parsedDate.getTime()) ||
      parsedDate.getFullYear() !== Number(year) ||
      parsedDate.getMonth() !== Number(month) - 1 ||
      parsedDate.getDate() !== Number(day)
    ) {
      return null
    }

    return parsedDate
  }

  const isoFormat = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!isoFormat) return null

  const [, year, month, day] = isoFormat
  const parsedDate = new Date(Number(year), Number(month) - 1, Number(day))
  if (
    Number.isNaN(parsedDate.getTime()) ||
    parsedDate.getFullYear() !== Number(year) ||
    parsedDate.getMonth() !== Number(month) - 1 ||
    parsedDate.getDate() !== Number(day)
  ) {
    return null
  }

  return parsedDate
}

export const getAgeFromBirthDate = (value: string) => {
  const parsedDate = parseBirthDate(value)
  if (!parsedDate) return null

  const today = new Date()
  let age = today.getFullYear() - parsedDate.getFullYear()
  const monthDifference = today.getMonth() - parsedDate.getMonth()

  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < parsedDate.getDate())) {
    age -= 1
  }

  return age
}

export const getAge = (birthDate: string) => getAgeFromBirthDate(birthDate)

export const deriveAddressComponents = (
  addressStr: string,
  current: Omit<AddressFields, 'addressLine1'>
) => {
  if (current.city && current.state && current.zipCode) return current
  const zipMatch = addressStr.match(/\b(\d{5})(?:-\d{4})?\b/)
  if (!zipMatch) return current
  const zip = zipMatch[1]
  const info = zipcodes.lookup(zip)
  return {
    city: current.city || info?.city || '',
    state: current.state || info?.state || '',
    zipCode: current.zipCode || zip
  }
}

export const getBirthDateForNativePicker = (birthDate: string) => {
  if (!birthDate) return ''
  const [month, day, year] = birthDate.split('/')
  if (!month || !day || !year || year.length !== 4) return ''
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

export const getUnder18BirthDateBounds = () => {
  const today = new Date()
  const latestUnder18BirthDate = new Date(today)
  latestUnder18BirthDate.setFullYear(latestUnder18BirthDate.getFullYear() - 18)
  latestUnder18BirthDate.setDate(latestUnder18BirthDate.getDate() + 1)

  const toIsoDate = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  return {
    min: toIsoDate(latestUnder18BirthDate),
    max: toIsoDate(today)
  }
}

export const capitalizeFirst = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value

export const formatBirthDateForApi = (value: string) => {
  const [month, day, year] = value.split('/')
  if (month && day && year?.length === 4) {
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }

  return value || undefined
}

export const getRegisterErrorMessage = (error: unknown, fallbackMessage: string) => {
  const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message

  return capitalizeFirst(
    apiMessage ??
      (error instanceof Error ? error.message : fallbackMessage || ENROLLMENT_COMMON_MESSAGES.submitShortFailed)
  )
}

const currentYear = new Date().getFullYear()

export const MONTHS = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' }
]

export const YEARS = Array.from({ length: 5 }, (_, index) => ({
  label: String(currentYear + index),
  value: String(currentYear + index)
}))
